import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/utils/api";
import { fmtCurrency, fmtDate, StatusBadge } from "@/utils/format";
import { toast } from "sonner";
import { B2B } from "@/constants/testIds";
import { ShoppingCart, FileText, TrendingUp, Package } from "lucide-react";

const BALANCE_TYPE_LABELS = {
  borc: "Borç",
  alacak: "Alacak",
  sifir: "Sıfır",
};

export default function B2BDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cari, setCari] = useState(null);
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      if (user?.cari_id) {
        const [cariRes, sipRes] = await Promise.all([
          api.get(`/cariler/${user.cari_id}`),
          api.get("/siparisler/", { params: { per_page: 5 } }),
        ]);
        setCari(cariRes.data);
        setSiparisler(sipRes.data.items);
      }
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error("Veriler yüklenemedi");
      }
    } finally {
      setLoading(false);
    }
  }, [user?.cari_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#0D1B3E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Welcome */}
      <div className="bg-[#0D1B3E] text-white rounded-xl p-5">
        <div className="text-xs text-white/60 uppercase tracking-wider mb-1">Hoşgeldiniz</div>
        <div className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
          {cari?.unvan || user?.name || "B2B Müşteri"}
        </div>
        {cari && <div className="text-xs text-white/50 mt-0.5">Cari Kodu: {cari.kod}</div>}
      </div>

      {/* Account Summary */}
      {cari && (
        <div className="grid grid-cols-2 gap-3">
          <div data-testid={B2B.bakiyeDisplay} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={15} className="text-slate-400" />
              <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">Güncel Bakiye</span>
            </div>
            <div className="flex items-baseline gap-2 text-[#0D1B3E]">
              <span className="text-xl font-bold tabular-nums">{fmtCurrency(Math.abs(Number(cari.bakiye) || 0))}</span>
              <span className="text-xs font-semibold">{BALANCE_TYPE_LABELS[cari.bakiye_tipi] || BALANCE_TYPE_LABELS.sifir}</span>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={15} className="text-slate-400" />
              <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">Fiyat Listesi</span>
            </div>
            <div className="text-xl font-bold text-[#0D1B3E]">
              {cari.fiyat_listesi || "Perakende"}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/b2b/stok")}
          className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:border-[#0D1B3E]/30 transition-all text-left">
          <div className="w-10 h-10 bg-[#0D1B3E]/10 rounded-lg flex items-center justify-center">
            <Package size={18} className="text-[#0D1B3E]" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Stok Listesi</div>
            <div className="text-xs text-slate-400">Ürünleri incele</div>
          </div>
        </button>
        <button onClick={() => navigate("/b2b/ekstre")}
          className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:border-[#0D1B3E]/30 transition-all text-left">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <FileText size={18} className="text-slate-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Ekstre</div>
            <div className="text-xs text-slate-400">Hesap hareketleri</div>
          </div>
        </button>
      </div>

      {/* Recent Orders */}
      {siparisler.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart size={15} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-800">Son Siparişler</span>
            </div>
          </div>
          {siparisler.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 py-3 border-b border-slate-50 last:border-0">
              <div>
                <div className="text-sm font-medium text-slate-800">{s.siparis_no}</div>
                <div className="text-xs text-slate-400">{fmtDate(s.tarih)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold tabular-nums text-slate-700">{fmtCurrency(s.toplam_tutar)}</span>
                <StatusBadge status={s.durum} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
